'use client'

import { useEffect, useState } from 'react'

import { useReveal } from '@/lib/hooks/use-reveal'
import {
  Enterprise as Building2,
  Calculator,
  Education as GraduationCap,
  Building as School,
  Group as Users,
  Wallet,
  type CarbonIconType as LucideIcon,
} from '@carbon/icons-react'

import {
  BrandMark,
  CELL,
  GRID,
  H,
  IMG_H,
  IMG_W,
  TileMosaic,
  W,
  buildField,
  cropFor,
  mulberry32,
  type Composition,
} from '@/components/pattern-field'
import { cn } from '@/lib/utils'

/**
 * Card thumbnail for the product suite, split in two by a slanted divider.
 *
 * LEFT — the brand side: the shared tile field (hairline grid, sparse mosaic
 * of gradient crops) with the Euclideum mark set on it. See
 * `@/components/pattern-field`, which BlogThumb draws from too.
 *
 * RIGHT — the product side: the gradient artwork itself as the backdrop,
 * with the product's icon (the same lucide glyph its solutions-deck card
 * carries) drawn directly over it — no chip, no backing shape.
 *
 * Tile crops come from a seeded PRNG that re-rolls once per page load; the
 * server render uses a fixed seed and the client re-rolls after mount,
 * which keeps hydration clean.
 */

const CY = H / 2

// Slanted divider: top edge crossing at SPLIT_T, bottom edge at SPLIT_B,
// with a clear gap between the two parts so the card surface shows through
// as the dividing slash.
const SPLIT_T = 296
const SPLIT_B = 248
const DIV = 5

// Visual centres of the two parts.
const LX = 134
const RX = 378

const MARK_H = 100
const ICON_S = 96

export type ProductThumbVariant =
  'udc' | 'accounting' | 'lms' | 'hrms' | 'payroll' | 'school'

/** Same product → artwork pairing as the solutions deck. */
const ART: Record<ProductThumbVariant, string> = {
  udc: '/gradients/02.webp',
  accounting: '/gradients/01.webp',
  lms: '/gradients/04.webp',
  hrms: '/gradients/03.webp',
  payroll: '/gradients/06.webp',
  school: '/gradients/05.webp',
}

/** Distinct PRNG stream per variant, so cards never share a mosaic. */
const VARIANT_INDEX: Record<ProductThumbVariant, number> = {
  udc: 1,
  accounting: 2,
  lms: 3,
  hrms: 4,
  payroll: 5,
  school: 6,
}

/** The same icon each product carries in the solutions deck. */
const ICONS: Record<ProductThumbVariant, LucideIcon> = {
  udc: Building2,
  accounting: Calculator,
  lms: GraduationCap,
  hrms: Users,
  payroll: Wallet,
  school: School,
}

/** Icon ink per product — the deck's panel colour, deep enough to hold its
    own on the pastel artwork with no backing shape. Accounting is the one
    exception: its artwork is saturated royal blue right where the icon
    sits, so it inverts to a near-white ink instead of navy-on-blue. */
const PANEL: Record<ProductThumbVariant, string> = {
  udc: '#065f46',
  accounting: '#93c5fd',
  lms: '#9a3412',
  hrms: '#9f1239',
  payroll: '#115e59',
  school: '#6b21a8',
}

// Sparse fields — the right side of each map matters less now (the divider
// clips it away), but the marks there still feed the left edge of the slash.
const COMPOSITIONS: Record<ProductThumbVariant, Composition> = {
  // A band gathering onto one spine through the middle.
  udc: {
    map: [
      '...........',
      '.1....11...',
      '11...+.....',
      '.........11',
      '1...11.....',
      '......1..1.',
      '...........',
    ],
  },
  // Weight climbing to the upper right.
  accounting: {
    map: [
      '.......1.+.',
      '....1....1.',
      '..........1',
      '1......11..',
      '.1.........',
      '......1....',
      '11.........',
    ],
  },
  // Two banded runs with quiet rows between — leaves of a book.
  lms: {
    map: [
      '...........',
      '11...+..11.',
      '...........',
      '.......1...',
      '....11..1..',
      '...........',
      '.1....1....',
    ],
  },
  // An even, repeating rhythm — the same module over and over.
  hrms: {
    map: [
      '1.....1....',
      '...1......1',
      '+..........',
      '1.........1',
      '....1....1.',
      '...........',
      '.1...1...1.',
    ],
  },
  // Two standing columns — value moving through, out and back.
  payroll: {
    map: [
      '.1......1..',
      '.1........1',
      '.1...+.....',
      '........1..',
      '11.........',
      '.....1...1.',
      '.1......1..',
    ],
  },
  // Weighted to a broad base, thinning upward.
  school: {
    map: [
      '...........',
      '....1.....1',
      '....1......',
      '1........1.',
      '.1....1....',
      '11...+..11.',
      '.1.1...11..',
    ],
  },
}

/**
 * Reveal sequencer. Every thumbnail watches its own viewport entry, so two
 * cards standing side by side would otherwise start on the same frame and the
 * page reads as one big simultaneous pop.
 *
 * Claims arriving within one short window are batched, sorted by where the
 * card actually sits (rows, then left to right — observer callback order is
 * not spatial), and dealt start times one CARD_STAGGER apart off a shared
 * tail. A card that scrolls in later, after the queue has drained, starts at
 * once.
 */
const CARD_STAGGER = 380

/* No card ever waits more than two slots: a large batch sequences its first
   three and the rest join the third wave, so the queue can never make the
   page feel like it is buffering. */
const MAX_QUEUE = CARD_STAGGER * 2

let revealTail = 0
let revealBatch: { el: Element; resolve: (delay: number) => void }[] = []
let revealFlush: ReturnType<typeof setTimeout> | null = null

function claimReveal(el: Element, resolve: (delay: number) => void) {
  revealBatch.push({ el, resolve })

  revealFlush ??= setTimeout(() => {
    revealFlush = null
    const batch = revealBatch
    revealBatch = []

    batch.sort((a, b) => {
      const ra = a.el.getBoundingClientRect()
      const rb = b.el.getBoundingClientRect()
      // Coarse row bucket first, so a few px of vertical jitter between
      // cards in the same visual row cannot reorder the sweep.
      return (
        Math.round(ra.top / 120) - Math.round(rb.top / 120) || ra.left - rb.left
      )
    })

    const now = performance.now()
    for (const claim of batch) {
      const start = Math.min(Math.max(now, revealTail), now + MAX_QUEUE)
      revealTail = start + CARD_STAGGER
      claim.resolve(Math.round(start - now))
    }
  }, 40)
}

/* The six grid cells the mark occupies (cols 2–3, rows 2–4 around LX,CY)
   stay empty. */
const MARK_ZONE = {
  x0: 2 * CELL,
  x1: 4 * CELL,
  y0: 2 * CELL,
  y1: 5 * CELL,
}

const TILES = Object.fromEntries(
  (Object.keys(COMPOSITIONS) as ProductThumbVariant[]).map((k) => [
    k,
    buildField(COMPOSITIONS[k], MARK_ZONE, `ProductThumb ${k}`),
  ])
) as Record<ProductThumbVariant, ReturnType<typeof buildField>>

export function ProductThumb({
  variant,
  className,
}: {
  variant: ProductThumbVariant
  className?: string
}) {
  const uid = `pt-${variant}`
  const art = ART[variant]
  const Icon = ICONS[variant]

  // Fixed seed on the server and first client paint; a real roll right after
  // mount. Hydration sees identical markup, then the mosaic reshuffles.
  const [seed, setSeed] = useState(1)
  useEffect(() => {
    setSeed(1 + Math.floor(Math.random() * 0xffff))
  }, [])

  // The field assembles itself the first time the card is actually on screen.
  // Inside the showcase carousel that means each thumbnail builds as it slides
  // in, rather than every card having played its entrance off-stage.
  // "Has this card been reached yet" is decided by the shared fail-safe hook.
  // The gate used to be a private observer with `threshold: 0.1` and a -25%
  // bottom margin that disconnected on first hit — so any card the observer
  // sampled past (fast scroll, an anchor jump, a reload that restored scroll)
  // never revealed, and its mosaic sat permanently empty. The hook keeps the
  // same focus gate via rootMargin but adds a mount-time geometry check and a
  // scroll sweep, so a missed sample can no longer strand the card.
  const { ref: rootRef, shown: reached } = useReveal<SVGSVGElement>(0.25)
  const [shown, setShown] = useState(false)
  const [groupDelay, setGroupDelay] = useState(0)

  useEffect(() => {
    if (!reached || shown) return
    const el = rootRef.current
    if (!el) {
      setShown(true)
      return
    }
    // The sequencer still stages the group so cards take turns rather than
    // all building at once.
    claimReveal(el, (delay) => {
      setGroupDelay(delay)
      setShown(true)
    })
  }, [reached, shown, rootRef])

  const rand = mulberry32(seed * 131 + VARIANT_INDEX[variant])
  const crops = TILES[variant].map((t) => cropFor(rand, t))

  return (
    <svg
      ref={rootRef}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid slice"
      // currentColor carries the hairline grid.
      className={cn(
        'h-full w-full text-zinc-900/[0.08] dark:text-white/[0.07]',
        'pattern-anim',
        shown && 'pattern-shown',
        className
      )}
      style={{ '--pattern-delay': `${groupDelay}ms` } as React.CSSProperties}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* The slanted split — two clips either side of a clear slash. */}
        <clipPath id={`${uid}-left`}>
          <polygon
            points={`0,0 ${SPLIT_T - DIV},0 ${SPLIT_B - DIV},${H} 0,${H}`}
          />
        </clipPath>
        <clipPath id={`${uid}-right`}>
          <polygon
            points={`${SPLIT_T + DIV},0 ${W},0 ${W},${H} ${SPLIT_B + DIV},${H}`}
          />
        </clipPath>
      </defs>

      {/* ── Left part: the existing pattern + the mark ── */}
      <g clipPath={`url(#${uid}-left)`}>
        {/* Grid and mark are static — only the mosaic tiles animate in. */}
        <path d={GRID} stroke="currentColor" strokeWidth="1" fill="none" />
        <TileMosaic tiles={TILES[variant]} crops={crops} art={art} animate />
        {/* The mark takes no part in the entrance — it is simply there, the
            fixed point the tiles assemble around, its silhouette filled with
            the same artwork the tiles crop from. */}
        <BrandMark cx={LX} cy={CY} height={MARK_H} art={art} />
      </g>

      {/* ── Right part: the artwork directly, icon drawn straight on it ── */}
      <g clipPath={`url(#${uid}-right)`}>
        <svg
          x={SPLIT_B}
          y={0}
          width={W - SPLIT_B}
          height={H}
          viewBox={`0 0 ${IMG_W} ${IMG_H}`}
          preserveAspectRatio="xMidYMid slice"
        >
          <image href={art} width={IMG_W} height={IMG_H} />
        </svg>
        {/* Dark theme mutes the pastel art, same move as the deck's veil */}
        <rect
          x={SPLIT_B}
          y={0}
          width={W - SPLIT_B}
          height={H}
          className="hidden fill-black/25 dark:block"
        />
        <Icon
          x={RX - ICON_S / 2}
          y={CY - ICON_S / 2}
          width={ICON_S}
          height={ICON_S}
          style={{ color: PANEL[variant] }}
        />
      </g>
    </svg>
  )
}

export default ProductThumb
